import type { RootState } from 'src/redux/store';
import type { ImapSettings, SmtpSettings, IChannelState } from 'src/types/checkout';

import { toast } from 'sonner';
import { isEqual } from 'es-toolkit';
import { useLocalStorage } from 'minimal-shared/hooks';
import { useMemo, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname, useSearchParams } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';

import { ChannelContext } from './channel-context';
import { useCreateChannelMutation } from '../slice/channelApi';
import { buildChannelFormData } from './channel-form-builders';
import {
  ERROR_MAP,
  initialState,
  CHANNEL_STEPS,
  getAssignedPayload,
  CHECKOUT_STORAGE_KEY,
} from './channel-utils';
import {
  shouldRedirect,
  shouldCreateChannel,
  handleStepTransition,
  shouldCreateEmailOrSMS,
} from './channel-step-handlers';

// ----------------------------------------------------------------------

type CheckoutProviderProps = {
  children: React.ReactNode;
};

export function ChannelProvider({ children }: CheckoutProviderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { t } = useTranslate('translation');
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);

  const activeStep = pathname.includes('') ? Number(searchParams.get('step')) : null;
  const isUserInStep = pathname.includes('step') || pathname.includes('edit');

  const [createChannel, { isLoading }] = useCreateChannelMutation();

  const { state, setState, setField } = useLocalStorage<IChannelState>(
    CHECKOUT_STORAGE_KEY,
    initialState,
    { initializeWithValue: false }
  );

  const channelType = state.channelType ?? '';
  const emailProviderType = state.emailProviderType ?? '';

  const canReset = !isEqual(state, initialState);

  const steps = useMemo(
    () => CHANNEL_STEPS(t, channelType, emailProviderType),
    [channelType, emailProviderType, t]
  );

  const completed = activeStep === steps.length;

  const handleResetState = useCallback(() => {
    setState(initialState);
  }, [setState]);

  const createChannelFunc = useCallback(
    async (formData: any, assignedPayload: any) => {
      try {
        const res: any = await createChannel({ ...formData, ...assignedPayload }).unwrap();
        setField('channelCreatedResponse', res.data);
        toast.success(t('CHANNEL_CREATED_SUCCESSFULLY'));
        return true;
      } catch (err: any) {
        const errorMessage = err?.data?.message;
        const translatedError = errorMessage && ERROR_MAP[errorMessage];
        toast.error(
          translatedError ? t(translatedError) : errorMessage || t('SOMETHING_WENT_WRONG')
        );
        return false;
      }
    },
    [createChannel, setField, t]
  );

  const onChangeStep = useCallback(
    async (type: 'back' | 'next' | 'go', step?: number) => {
      if (type === 'back') {
        sessionStorage.setItem('channel_nav_direction', 'back');
      } else if (type === 'next') {
        sessionStorage.setItem('channel_nav_direction', 'forward');
      }

      const stepNumbers = {
        back: (activeStep ?? 0) - 1,
        next: (activeStep ?? 0) + 1,
        go: step ?? 0,
      };

      const targetStep = stepNumbers[type];

      if (targetStep === -1) {
        handleResetState();
        return;
      }

      handleStepTransition(targetStep, state, setField, loginApiData);

      if (shouldCreateChannel(targetStep, state.channelType)) {
        const formData = buildChannelFormData(state);
        if (formData) {
          const assignedPayload = getAssignedPayload(state.assignUsers);
          const success = await createChannelFunc(formData, assignedPayload);
          if (!success) return;
        }
      }

      if (shouldCreateEmailOrSMS(targetStep, state)) {
        const formData = buildChannelFormData(state);
        if (formData) {
          const assignedPayload = getAssignedPayload(state.assignUsers);
          const success = await createChannelFunc(formData, assignedPayload);
          if (!success) return;
        }
      }

      if (shouldRedirect(targetStep, state)) {
        router.replace(paths.channel.root);
        handleResetState();
        return;
      }

      const queryString = new URLSearchParams({ step: `${targetStep}` }).toString();
      const redirectPath =
        targetStep === 0 ? paths.channel.step : `${paths.channel.step}?${queryString}`;
      router.push(redirectPath);
    },
    [activeStep, createChannelFunc, handleResetState, router, setField, state, loginApiData]
  );

  const handleStepHeader = useCallback(() => {
    if (activeStep === null) return t('STEP');

    return steps[activeStep]?.header || t('STEP');
  }, [activeStep, steps, t]);

  const handlers = useMemo(
    () => ({
      onSelectChannel: (value: string) => {
        setField('channelType', value);
        if (value === 'SMS') {
          setField('SMSProviderType', 'bms_api');
        }
      },
      onSelectEmailProvider: (value: string) => setField('emailProviderType', value),
      onSMSProvider: (value: string) => setField('SMSProviderType', value),
      handleAddUser: (value: any[]) => setField('assignUsers', value),
      handleAddUserType: (value: string) => setField('assignedType', value),
      handleChannelName: (value: string) => setField('channelName', value),
      handleWhatsappPin: (value: string) => setField('whatsappPin', value),
      handleEmailChannelDtls: (value: { channelName: string; email: string }) =>
        setField('emailChannelDtls', value),
      handleFaceBookDeteail: (value: any) => setField('facebookDetails', value),
      handleLineDetail: (value: any) => setField('lineDetails', value),
      handleGoogleDetail: (value: string) => setField('googleEmailDetails', { code: value }),
      handleTelegramDetail: (value: string) => setField('telegramBotToken', value),
      handleLiveChatChannelDtls: (value: any) => setField('LiveChatDeatil', value),
      handleSmsDetails: (value: any) => {
        setField('channelName', value.channelName);
        setField('smsChannelDtls', value);
      },
      handleMicrosoftDetail: (value: string) => setField('microsoftEmailDetails', { code: value }),
      handleMicrosoftTeamsDetail: (value: string) =>
        setField('microsoftTeamsDetails', { code: value }),
      handleSMTPDetails: (value: SmtpSettings) => setField('smtpDetails', value),
      handleOnChannelCreated: (value: any) => setField('channelCreatedResponse', value),
      handleIMAPDetails: (value: ImapSettings) => setField('imapDetails', value),
    }),
    [setField]
  );

  const memoizedValue = useMemo(
    () => ({
      state,
      setState,
      setField,
      activeStep,
      onChangeStep,
      steps,
      canReset,
      completed,
      handleStepHeader,
      isLoading,
      ...handlers,
    }),
    [
      state,
      setState,
      setField,
      activeStep,
      onChangeStep,
      canReset,
      completed,
      handleStepHeader,
      isLoading,
      handlers,
      steps,
    ]
  );

  useEffect(() => {
    if (!isUserInStep) handleResetState();
  }, [isUserInStep, handleResetState]);

  return <ChannelContext.Provider value={memoizedValue}>{children}</ChannelContext.Provider>;
}
